import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Card, CardContent, Container, TextField, Typography, Alert, CircularProgress, Grid, Autocomplete } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import axios from 'axios';

interface LegalProvision {
  entity_id: string;
  full_name: string;
  abbreviation: string;
}

/**
 * Standalone Semantic Search Admin Dashboard for Microservice
 * - No dependencies on internal-ui
 * - Manages embeddings population and status
 * - Runs independently at http://microservice:3110
 */
export default function SemanticSearchAdmin() {
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [selectedProvisionId, setSelectedProvisionId] = useState<string>('');
  const [processingType, setProcessingType] = useState<'embed' | 'overwrite' | 'all-unembedded' | 'populate-test' | null>(null);
  const [populatingEmbeddings, setPopulatingEmbeddings] = useState(false);
  const [provisionsWithoutCoverage, setProvisionsWithoutCoverage] = useState<Set<string>>(new Set());
  const [provisions, setProvisions] = useState<LegalProvision[]>([]);
  const [loading, setLoading] = useState(true);
  const embeddingsFetchedRef = useRef(false);

  // Fetch provisions and embedding status
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get all provisions
        const provisionsResponse = await axios.get('/api/search/provisions');
        const pList = (provisionsResponse.data || []).map((item: any) => ({
          entity_id: item.entity_id || item.entityId,
          full_name: item.full_name || item.fullName || item.name,
          abbreviation: item.abbreviation || item.abbr || '',
        }));
        setProvisions(pList);

        // Get embedding status
        const statusResponse = await axios.get('/api/search/admin/embedding-status');
        const withoutCoverage = new Set(statusResponse.data.provisionsWithoutFullCoverage || []);
        setProvisionsWithoutCoverage(withoutCoverage);

        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load provisions and embedding status';
        setError(errorMessage);
        setLoading(false);
      }
    };

    if (!embeddingsFetchedRef.current) {
      embeddingsFetchedRef.current = true;
      fetchData();
    }
  }, []);

  const handlePopulateTestEmbeddings = async () => {
    setPopulatingEmbeddings(true);
    setError(null);
    setStatus('Starting embedding population for test provisions...');
    setProcessingType('populate-test');

    try {
      const response = await axios.post('/api/search/admin/populate-test');
      if (response.data.success || response.data.summary) {
        const { summary } = response.data;
        setStatus(`✓ Population completed! Generated ${summary.processed} embeddings (${summary.total} total, ${summary.failed} failed)`);
      } else {
        setError(`Population failed: ${JSON.stringify(response.data)}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Population failed';
      setError(message);
    } finally {
      setPopulatingEmbeddings(false);
      setProcessingType(null);
    }
  };

  const handlePopulateByIds = async (entityIds: string[]) => {
    setPopulatingEmbeddings(true);
    setError(null);
    setStatus('Starting embedding for selected provisions...');
    setProcessingType('embed');

    try {
      const response = await axios.post('/api/search/admin/populate-by-ids', {
        provisionEntityIds: entityIds,
      });

      if (response.data.success || response.data.summary) {
        const { summary } = response.data;
        setStatus(`✓ Population completed! Generated ${summary.processed} embeddings (${summary.total} total, ${summary.failed} failed)`);
        // Refresh status
        const statusResponse = await axios.get('/api/search/admin/embedding-status');
        setProvisionsWithoutCoverage(new Set(statusResponse.data.provisionsWithoutFullCoverage || []));
      } else {
        setError(`Population failed: ${JSON.stringify(response.data)}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Population failed';
      setError(message);
    } finally {
      setPopulatingEmbeddings(false);
      setProcessingType(null);
    }
  };

  const handleEmbedProvision = async () => {
    if (!selectedProvisionId) {
      setError('Please select a legal provision');
      return;
    }
    await handlePopulateByIds([selectedProvisionId]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" sx={{ mb: 4, fontWeight: 'bold' }}>
        🚀 Semantic Search Microservice Admin
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {status && <Alert severity="success" sx={{ mb: 2 }}>{status}</Alert>}

      <Grid container spacing={3}>
        {/* Bulk Operations */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                📊 Bulk Operations
              </Typography>

              <Button
                variant="contained"
                color="warning"
                onClick={handlePopulateTestEmbeddings}
                disabled={populatingEmbeddings}
                fullWidth
                sx={{ mb: 1 }}
              >
                {processingType === 'populate-test' ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Populating...
                  </>
                ) : (
                  'Populate Test Embeddings'
                )}
              </Button>

              <Button
                variant="contained"
                color="success"
                onClick={() => handlePopulateByIds(provisions.map(p => p.entity_id))}
                disabled={populatingEmbeddings}
                fullWidth
              >
                Populate All Provisions
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Individual Selection */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                📋 Individual Provision
              </Typography>

              <Autocomplete
                options={provisions}
                loading={loading}
                getOptionLabel={(option) => `${option.abbreviation} - ${option.full_name}`}
                value={provisions.find((p) => p.entity_id === selectedProvisionId) || null}
                onChange={(event, newValue) => {
                  setSelectedProvisionId(newValue?.entity_id || '');
                  setError(null);
                  setStatus(null);
                }}
                disableListWrap
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search and select..." size="small" sx={{ mb: 2 }} />
                )}
                renderValue={(value) => {
                  if (!value) return '';
                  const needsEmbeddings = provisionsWithoutCoverage.has(value.entity_id);
                  return (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {needsEmbeddings ? (
                        <Cancel sx={{ width: 18, height: 18, color: '#ff6b6b', flexShrink: 0 }} />
                      ) : (
                        <CheckCircle sx={{ width: 18, height: 18, color: '#51cf66', flexShrink: 0 }} />
                      )}
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{value.abbreviation}</Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>{value.full_name}</Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderOption={(props, option) => {
                  const needsEmbeddings = provisionsWithoutCoverage.has(option.entity_id);
                  return (
                    <Box {...props} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {needsEmbeddings ? (
                        <Cancel sx={{ width: 20, height: 20, color: '#ff6b6b', flexShrink: 0 }} />
                      ) : (
                        <CheckCircle sx={{ width: 20, height: 20, color: '#51cf66', flexShrink: 0 }} />
                      )}
                      <Box>
                        <Typography variant="body2">{option.abbreviation}</Typography>
                        <Typography variant="caption">{option.full_name}</Typography>
                      </Box>
                    </Box>
                  );
                }}
              />

              <Button
                variant="contained"
                onClick={handleEmbedProvision}
                disabled={!selectedProvisionId || populatingEmbeddings}
                fullWidth
              >
                Embed Selected
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Info */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                📊 Status
              </Typography>
              <Typography variant="body2">
                Total Provisions: {provisions.length}<br />
                Without Full Coverage: {provisionsWithoutCoverage.size}<br />
                Fully Embedded: {provisions.length - provisionsWithoutCoverage.size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
